import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@teach/ui'
import type { CourseWithoutBlocks } from '@/types/course'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setDialogOpen, setSettingsTab } from '@/features/editor/editorSlice'
import type { ImageSlot } from '@/features/ai/images/lib/targetKeys'
import { useImageQuote } from '@/features/ai/useImageQuote'
import { useStartImageGeneration } from '@/features/ai/useStartImageGeneration'
import { useReserveReturning } from '@/features/ai/useReserveReturning'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { useHasUnsavedBlockDraft } from './aiSidebar/useUnsavedBlockGate'
import { DrawImagesBody } from './DrawImagesBody'
import { canStartDraw } from './drawImages/canStartDraw'
import { DrawImagesModes } from './drawImages/DrawImagesModes'
import { useDrawMode } from './drawImages/useDrawMode'
import { useRefreshTargets } from './drawImages/useRefreshTargets'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
  /** Слот, с которого открыли диалог: из итога перевода приходит второй язык.
   *  Он же открывает диалог сразу в ручном режиме — см. `useDrawMode`. */
  initialSlot?: ImageSlot
}

/**
 * Запуск отрисовки — диалог, а не клик в один приём: прогон платный, и автор
 * обязан увидеть размер плана и его цену до списания.
 *
 * Два режима. «Нарисовать всё» — числа берём У СЕРВЕРА
 * (`image_generation.auto_targets`/`auto_cost`), тело `targets: null`.
 * «Выбрать места» — план собирает `TargetPicker`, тело несёт массив ключей.
 * Гейты запуска ОБЩИЕ для обоих режимов (`canStartDraw`): вторых копий условий
 * не заводим.
 */
export const DrawImagesDialog = ({ course, initialSlot }: Props) => {
  const m = useMessages(messages)
  const dispatch = useAppDispatch()
  const open = useAppSelector((s) => s.editor.dialogs.drawImagesOpen)
  const quote = useImageQuote(course.id, !open)
  const { start, isStarting, failure, reset } = useStartImageGeneration()
  // E9: область прогона считает СЕРВЕР (`auto_targets`) — клиент не знает,
  // какие блоки затронет отрисовка, поэтому пересечь её с грязным блоком
  // нельзя (в отличие от E8, где область известна заранее). Гейт грубее:
  // гасим старт при ЛЮБОЙ несохранённой правке открытого блока. В ручном
  // режиме он тот же самый — своей копии условия там нет.
  const hasUnsavedBlockDraft = useHasUnsavedBlockDraft()
  // Признак «резерв прошлого прогона ещё возвращается» считает ДИАЛОГ (id
  // курса есть только здесь) и отдаёт телу пропом — вторая копия гейта в
  // `DrawImagesBody` запрещена тем же правилом, что и остальные признаки.
  const reserveReturning = useReserveReturning(course.id)
  const isFrozen = useIsCourseFrozen(course.id)
  const { mode, pick, plan, setPlan, manualSeen, reset: resetMode } = useDrawMode(initialSlot)
  useRefreshTargets(failure, course.id)
  const [pilotSets, setPilotSets] = useState(0)
  // Тумблер живёт только в авто-режиме: в ручном набор рисовался бы для
  // непредсказуемой «первой картинки плана». Заказ, которого автор не видит,
  // унести с собой нельзя — в ручном режиме он принудительно ноль.
  const effectivePilotSets = mode === 'auto' ? pilotSets : 0

  const hasStyle = Boolean(course.imageStyle?.trim())
  // Числа плана приходят ТОЛЬКО из сметы. Пока её нет — неизвестно ни сколько
  // картинок нарисуется, ни во сколько это обойдётся, и живая кнопка списывала
  // бы кредиты вслепую. `autoTargets === undefined` покрывает оба случая: смета
  // ещё летит и смета упала (при отказе состояние иначе вечное). Ручной режим
  // без сметы тоже мёртв: из неё берутся и цена картинки, и потолок прогона.
  const hasQuote = quote.autoTargets !== undefined
  const quoteFailed = !hasQuote && !quote.isLoading
  const nothingToDraw = quote.autoTargets === 0
  // Important 3 (финальное ревью): «стиль урегулирован» — ОДНО вычисление на
  // весь диалог. Раньше `hasStyle || effectivePilotSets > 0` считалось
  // заново в `canStartDraw`, в футере и (под видом `pilotSets > 0`) в
  // `DrawImagesBody` — три независимые копии одного условия уже разошлись
  // внутри этой же ветки (баннер обещал одно, кнопка рядом — другое).
  const styleSettled = hasStyle || effectivePilotSets > 0
  const canStart = canStartDraw({
    styleSettled,
    hasQuote,
    busyJob: quote.activeJob,
    isStarting,
    failure,
    hasUnsavedBlockDraft,
    isFrozen,
    hasPlan: mode === 'auto' ? !nothingToDraw : plan.length > 0,
  })

  const close = () => {
    reset()
    resetMode()
    setPilotSets(0)
    dispatch(setDialogOpen({ dialog: 'drawImagesOpen', open: false }))
  }

  const openStyle = () => {
    close()
    // Стиль картинок живёт во вкладке «Дизайн-система»; по умолчанию окно
    // настроек открывается на «Основном» (сбрасывается при каждом открытии).
    dispatch(setSettingsTab('design'))
    dispatch(setDialogOpen({ dialog: 'settingsOpen', open: true }))
  }

  const run = async () => {
    if (!canStart) return
    const ok = await start(course.id, mode === 'manual' ? plan : null, effectivePilotSets)
    // Прогон показывает AI-панель: она разворачивается сама, когда курс
    // перецепляется на новый jobId (useAutoOpenForJob) — а старт отрисовки это
    // именно перецепление, поверх незакрытого итога прошлого прогона.
    if (ok) close()
  }

  // «Задать стиль картинок» в футере прячется при заказанном наборе: кнопка —
  // это и есть путь к стилю, она закрывает диалог (и стирает отметку
  // тумблера) ради перехода в настройки, которые автор только что решил
  // обойти пилотами. Показывать её рядом с выбором «пусть стиль подберётся
  // сам» значило бы предлагать отменить этот выбор кнопкой, а не тумблером.
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{m.drawTitle}</DialogTitle>
          <DialogDescription>
            {mode === 'auto' ? m.drawAutoDescription : m.drawManualDescription}
          </DialogDescription>
        </DialogHeader>

        <DrawImagesModes
          mode={mode}
          onMode={pick}
          manualSeen={manualSeen}
          course={course}
          quote={quote}
          nothingToDraw={nothingToDraw}
          pilotSets={pilotSets}
          onPilotSetsChange={setPilotSets}
          onPlan={setPlan}
          initialSlot={initialSlot}
        />
        <DrawImagesBody
          quote={quote}
          hasStyle={hasStyle}
          styleSettled={styleSettled}
          quoteFailed={quoteFailed}
          isFrozen={isFrozen}
          reserveReturning={reserveReturning}
          failure={failure}
          onOpenStyle={openStyle}
          // Повтор — тот же запуск и под тем же гейтом, что кнопка «Нарисовать»:
          // предлагать его, когда старт всё равно погашен, было бы обманкой.
          onRetry={canStart ? () => void run() : undefined}
        />

        <DialogFooter>
          {styleSettled ? null : (
            <Button variant="outline" onClick={openStyle}>
              {m.drawSetStyle}
            </Button>
          )}
          <Button onClick={() => void run()} disabled={!canStart}>
            {isStarting ? m.drawStarting : m.draw}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
